import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeviceSession } from '@couch/shared';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { useSession } from '../../auth/session';
import { InstallHint } from './InstallHint';

const KEY = ['account-sessions'] as const;
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function DeviceRow({ device }: { device: DeviceSession }) {
  const queryClient = useQueryClient();
  const { signOut } = useSession();
  const navigate = useNavigate();
  const remove = useMutation({
    mutationFn: async () => {
      if (device.current) { await signOut(); await navigate('/login', { replace: true }); return; }
      await api.signOutDevice(device.id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
  const name = `${device.device} · ${device.browser}`;
  return (
    <tr>
      <td>{name} {device.current && <span className="badge badge-pending">This device</span>}</td>
      <td>{when(device.lastSeenAt)}</td>
      <td>
        <button type="button" className="button-quiet" aria-label={device.current ? 'Sign out here' : `Sign out ${name}`} disabled={remove.isPending} onClick={() => { remove.mutate(); }}>
          {device.current ? 'Sign out here' : 'Sign out'}
        </button>
        {remove.isError && <span role="alert" className="form-error"> {errorMessage(remove.error)}</span>}
      </td>
    </tr>
  );
}

/** Browsers where the user is signed in — phones included; only machines that compute are paired (doc 25). */
export function DevicesPanel() {
  const queryClient = useQueryClient();
  const sessions = useQuery({ queryKey: KEY, queryFn: api.listSessions });
  const others = useMutation({ mutationFn: api.signOutOtherDevices, onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }) });
  const list = sessions.data?.sessions ?? [];
  return (
    <section className="panel" aria-labelledby="devices-title">
      <h2 id="devices-title">Signed-in devices</h2>
      <p className="muted">Phones and other browsers where you signed in. They control your machines; the machines do the computing.</p>
      {sessions.isPending && <p role="status">Loading…</p>}
      {sessions.isError && <p role="alert" className="form-error">{errorMessage(sessions.error)}</p>}
      {list.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead><tr><th>Device</th><th>Last active</th><th><span className="visually-hidden">Actions</span></th></tr></thead>
            <tbody>{list.map((d) => <DeviceRow key={d.id} device={d} />)}</tbody>
          </table>
        </div>
      )}
      {list.length > 1 && (
        <p><button type="button" className="button-quiet" disabled={others.isPending} onClick={() => { others.mutate(); }}>Sign out all other devices</button></p>
      )}
      {others.isError && <p role="alert" className="form-error">{errorMessage(others.error)}</p>}
      <InstallHint />
    </section>
  );
}
